import { TextInput, TextInputProps, Layer } from '@carbon/react';
import classNames from 'classnames';
import { ReactNode, useEffect, useState } from 'react';
import { NumberFormatValues, NumericFormat } from 'react-number-format';
import style from './number-field.module.scss';

/**
 * Convert a number to its plain-decimal string form, expanding any scientific
 * notation. `String(5e-8)` returns `"5e-8"` which `NumericFormat` cannot
 * render as a fractional input — `numberToPlainString(5e-8)` returns
 * `"0.00000005"` instead. Plain values pass through unchanged.
 *
 * @param {number} n - The number to convert.
 * @return {string} Plain decimal representation, never scientific notation.
 */
export const numberToPlainString = (n: number): string => {
  const s = String(n);
  if (!/e/i.test(s)) return s;
  // toFixed() is intentional here: we need the plain-decimal *string* (not a
  // display-formatted number), so formatNumber() / Number(toFixed()) would
  // defeat the purpose by re-introducing scientific notation.
  // eslint-disable-next-line no-restricted-syntax
  return n.toFixed(20).replace(/\.?0+$/, '');
};

export interface NumberFieldProps extends Omit<
  TextInputProps,
  'value' | 'onChange' | 'defaultValue' | 'labelText'
> {
  value: number | null | undefined;
  onChange: (value: number | null) => void;
  /** Fires alongside onChange with the raw numeric string as typed. Use when
   *  validation needs the digits the user entered — the parsed number loses
   *  trailing zeros ("0.10000000" parses to 0.1). */
  onTextChange?: (text: string) => void;
  onBlur?: () => void;
  label?: ReactNode | string;
  light?: boolean;
  thousandSeparator?: string | boolean | undefined;
  allowNegative?: boolean;
  isAllowed?: (value: number) => boolean;
  decimalScale?: number;
  suffix?: string;
  prefix?: string;
  invalid?: boolean;
  invalidText?: string;
  required?: boolean;
}

export const NumberField = ({
  value,
  onChange,
  onTextChange,
  onBlur,
  label,
  light = false,
  thousandSeparator,
  isAllowed,
  decimalScale,
  suffix,
  prefix,
  invalid,
  invalidText,
  required,
  ...rest
}: NumberFieldProps) => {
  const thousandSeparatorValue =
    thousandSeparator === false ? undefined : thousandSeparator || ',';

  // Local display string preserves the user's raw typing across re-renders;
  // without it, `Number("0.0") === 0` would round-trip back to "0" and erase
  // the trailing decimal mid-entry. Only resync when the prop's numeric value
  // diverges from what the local string represents (external resets,
  // programmatic changes).
  const [displayString, setDisplayString] = useState<string>(
    value === null || value === undefined ? '' : numberToPlainString(value)
  );

  useEffect(() => {
    if (value === null || value === undefined || Number.isNaN(value)) {
      // Keep in-progress fragments like "." or "-" — they have no numeric
      // value yet, so the parent echoing null back must not erase them
      // (mirrors InputWithIcon's handling of intermediate typing states).
      setDisplayString((prev) => {
        return prev === '' || Number.isNaN(Number(prev)) ? prev : '';
      });
      return;
    }
    setDisplayString((prev) => {
      const parsed = Number(prev);
      if (Number.isNaN(parsed) || parsed !== value) {
        return numberToPlainString(value);
      }
      return prev;
    });
  }, [value]);

  const numericFormat = (
    <NumericFormat
      {...rest}
      isAllowed={
        isAllowed
          ? (values: NumberFormatValues) => isAllowed(Number(values.value))
          : undefined
      }
      type="text"
      labelText={
        label ? (
          <label>
            {label}
            {required && <span className={style.required}> (Required)</span>}
          </label>
        ) : (
          ''
        )
      }
      thousandSeparator={thousandSeparatorValue}
      decimalScale={decimalScale}
      suffix={suffix}
      prefix={prefix}
      onValueChange={(values) => {
        setDisplayString(values.value);
        onTextChange?.(values.value);
        // In-progress fragments ("", ".", "-") have no numeric value yet —
        // emit null rather than NaN (mirrors InputWithIcon's fragment guard).
        const parsed = Number(values.value);
        const newValue =
          values.value !== '' && !Number.isNaN(parsed) ? parsed : null;
        onChange(newValue);
      }}
      onBlur={onBlur}
      customInput={TextInput}
      invalid={invalid}
      invalidText={invalid ? invalidText : ''}
      valueIsNumericString
      value={displayString}
    />
  );

  return (
    <div className={classNames({ [style.light]: light })}>
      {!light ? <Layer>{numericFormat}</Layer> : numericFormat}
    </div>
  );
};
